// client/src/components/comunicacao/automatica/ComunicacaoAutomaticaForm.js
import React, { useState, useEffect } from 'react';
import { useComunicacaoConfig, useMutateComunicacaoConfig } from '../../../hooks/useComunicacaoConfig';
import useAlert from '../../../hooks/useAlert';
import { XCircle, Loader2 } from 'lucide-react';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';
import PeriodoEnvioFields from '../shared/PeriodoEnvioFields';

const STEP_LABELS = ['Configuração da Mensagem'];

// ✅ Cada domínio tem seu próprio vocabulário de evento — mesmo mapa de
// EVENTOS_POR_DOMINIO em server/models/ComunicacaoConfig.js. Domínio
// 'Sistema' só aparece para matriz (mensagens transacionais da própria
// ferramenta, ver TemplateMensagem.restritoMatriz). 'Cliente' é a
// confirmação de cadastro/recuperação de senha do site/conta.
const EVENTOS_POR_DOMINIO = {
  // ✅ 'NoShow' adicionado (set 2026) — falta sem aviso, mesmo evento novo
  // registrado no backend (ComunicacaoConfig.js::EVENTOS_POR_DOMINIO).
  Agendamento: ['Agendado', 'Confirmado', 'Pendente', 'Concluido', 'Cancelado', 'NoShow'],
  Pedido: ['confirmado', 'enviado', 'entregue', 'cancelado'],
  Sistema: ['conta_criada', 'conta_cancelada', 'pagamento_pendente', 'vencimento_de_plano', 'esqueci_senha'],
  // Comunicações do site/conta com o cliente final (cadastro/recuperação de
  // senha) — escopado por conta, igual Agendamento/Pedido. Se a conta não
  // configurar, o envio cai num padrão embutido (texto fixo, tenta
  // WhatsApp/SMS/Email na credencial disponível) — não fica sem enviar.
  // ✅ 'aniversario' — disparado pelo worker diário
  // aniversarioClienteService.js (server), não por uma ação do usuário
  // como os outros 2 eventos deste domínio.
  Cliente: ['cadastro_confirmacao', 'recuperacao_senha', 'aniversario'],
  // ✅ 'Concluído' (com acento) casa literalmente com PacoteCliente.status
  // — disparado quando a última sessão do pacote é usada.
  Pacote: ['Concluído'],
  // ✅ 'Concluido' casa com MatriculaCurso.status — disparado tanto pela
  // conclusão manual quanto pela conclusão automática ao terminar a
  // última aula do curso.
  Curso: ['Concluido'],
  // ✅ Resumo diário dos atendimentos do dia, enviado pro PROFISSIONAL
  // vinculado (não pro cliente) — escopado por conta, igual Agendamento.
  // periodoEnvio 'antes'/'depois' também é válido aqui (ver validação do
  // model no backend) — desloca em relação ao PRIMEIRO atendimento do
  // profissional naquele dia, não de um agendamento específico.
  Profissional: ['agendamentos_do_dia']
};

const EVENTO_LABELS = {
  Agendado: 'Agendado', Confirmado: 'Confirmado', Pendente: 'Pendente', Concluido: 'Concluído', Cancelado: 'Cancelado', NoShow: 'Falta sem aviso (No-show)',
  confirmado: 'Confirmado', enviado: 'Enviado', entregue: 'Entregue', cancelado: 'Cancelado',
  conta_criada: 'Conta Criada', conta_cancelada: 'Conta Cancelada', pagamento_pendente: 'Pagamento Pendente',
  vencimento_de_plano: 'Vencimento de Plano', esqueci_senha: 'Esqueci Senha',
  cadastro_confirmacao: 'Confirmação de Cadastro (Cliente)', recuperacao_senha: 'Recuperação de Senha (Cliente)',
  aniversario: 'Aniversário do Cliente',
  'Concluído': 'Concluído',
  agendamentos_do_dia: 'Agendamentos do Dia'
};

const CANAIS = ['WhatsApp', 'Email', 'SMS'];

const PERIODO_ENVIO_PADRAO = { momento: 'imediato', offsetMinutos: 0, diasSemana: [], janelaHorario: { inicio: null, fim: null } };

const ComunicacaoAutomaticaForm = ({ currentId, templates, onCloseForm, isMatrizAdmin }) => {
  const { setAlert } = useAlert();
  const { createConfig, updateConfig, isPending } = useMutateComunicacaoConfig();

  const { data: currentConfig, isLoading: isLoadingCurrent } = useComunicacaoConfig(currentId);

  const [form, setForm] = useState({
    tipo: 'Automatica',
    canal: 'WhatsApp',
    nome: '',
    template: '',
    dominio: 'Agendamento',
    evento: 'Agendado',
    ativo: true
  });

  // ✅ Override opcional de periodoEnvio — null/ausente no backend = herda
  // do template (ver comunicacaoConfigService.js::resolverPeriodoEnvio). Só
  // faz sentido pro domínio Agendamento (mesma regra do pre('validate') de
  // ComunicacaoConfig.js), então o toggle só aparece nesse caso.
  const [usarPeriodoProprio, setUsarPeriodoProprio] = useState(false);
  const [periodoEnvio, setPeriodoEnvio] = useState(PERIODO_ENVIO_PADRAO);

  useEffect(() => {
    if (currentConfig) {
      setForm({
        tipo: currentConfig.tipo || 'Automatica',
        canal: currentConfig.canal || 'WhatsApp',
        nome: currentConfig.nome || '',
        template: currentConfig.template?._id || '',
        dominio: currentConfig.dominio || 'Agendamento',
        evento: currentConfig.evento || 'Agendado',
        ativo: currentConfig.ativo !== false
      });
      setUsarPeriodoProprio(!!currentConfig.periodoEnvio);
      setPeriodoEnvio(currentConfig.periodoEnvio || PERIODO_ENVIO_PADRAO);
    }
  }, [currentConfig]);

  const handleChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Trocar de domínio reinicia o evento para o primeiro válido daquele domínio
      if (field === 'dominio') {
        next.evento = EVENTOS_POR_DOMINIO[value]?.[0] || '';
      }
      return next;
    });
    // periodoEnvio só é válido pros domínios Agendamento/Profissional —
    // trocar pra outro domínio limpa o override pra não submeter um
    // momento antes/depois inválido escondido atrás de um toggle que não
    // está mais visível.
    if (field === 'dominio' && !['Agendamento', 'Profissional'].includes(value)) {
      setUsarPeriodoProprio(false);
      setPeriodoEnvio(PERIODO_ENVIO_PADRAO);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.template) {
      setAlert('Selecione um template', 'danger');
      return;
    }

    if (!form.nome.trim()) {
      setAlert('O nome da configuração é obrigatório', 'danger');
      return;
    }

    if (!form.evento) {
      setAlert('Selecione um evento', 'danger');
      return;
    }

    const payload = {
      tipo: form.tipo,
      canal: form.canal,
      nome: form.nome,
      template: form.template,
      dominio: form.dominio,
      evento: form.evento,
      ativo: form.ativo,
      periodoEnvio: (['Agendamento', 'Profissional'].includes(form.dominio) && usarPeriodoProprio) ? periodoEnvio : null
    };

    try {
      if (currentId) {
        await updateConfig.mutateAsync({
          id: currentId,
          data: payload
        });
      } else {
        await createConfig.mutateAsync(payload);
      }
      onCloseForm();
    } catch (error) {
      // Erro já é tratado pelo hook
    }
  };

  const getEventoLabel = (evento) => EVENTO_LABELS[evento] || evento;

  const dominiosDisponiveis = isMatrizAdmin
    ? ['Agendamento', 'Pedido', 'Cliente', 'Pacote', 'Curso', 'Profissional', 'Sistema']
    : ['Agendamento', 'Pedido', 'Cliente', 'Pacote', 'Curso', 'Profissional'];

  if (isLoadingCurrent && currentId) {
    return (
      <div className="table-loading-state">
        <Loader2 size={24} className="animate-spin" />
        <span>Carregando configuração...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="form-header">
        <div>
          <h3 className="form-title">{currentId ? 'Editar Configuração' : 'Nova Configuração Automática'}</h3>
          {currentConfig?.createdAt && (
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--color-text-light, #6B7280)' }}>
              Criado em {new Date(currentConfig.createdAt).toLocaleDateString('pt-BR')} às {new Date(currentConfig.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
          <XCircle size={24} />
        </button>
      </div>

      <FormStepper currentStep={1} steps={STEP_LABELS} />

      <div className="form-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label className="form-section-title" htmlFor="ativo" style={{ marginBottom: 0 }}>
          Envio automático {form.ativo ? 'ativado' : 'desativado'}
        </label>
        <label className="toggle-switch">
          <input
            type="checkbox"
            id="ativo"
            checked={form.ativo}
            onChange={(e) => handleChange('ativo', e.target.checked)}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <fieldset className="form-section">
        <legend className="form-section-title">Dados da Mensagem</legend>
        <div className="form-grid-container-cols-2">
          <div className="form-group">
            <label htmlFor="nome" className="required">Nome da Configuração</label>
            <input
              type="text"
              id="nome"
              value={form.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
              placeholder="Ex: Confirmação de Agendamento"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dominio" className="required">Domínio</label>
            <select
              id="dominio"
              value={form.dominio}
              onChange={(e) => handleChange('dominio', e.target.value)}
              className="form-input"
              required
            >
              {dominiosDisponiveis.map(dominio => (
                <option key={dominio} value={dominio}>{dominio}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="evento" className="required">Evento</label>
            <select
              id="evento"
              value={form.evento}
              onChange={(e) => handleChange('evento', e.target.value)}
              className="form-input"
              required
            >
              {(EVENTOS_POR_DOMINIO[form.dominio] || []).map(evento => (
                <option key={evento} value={evento}>
                  {getEventoLabel(evento)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="canal" className="required">Canal de Comunicação</label>
            <select
              id="canal"
              value={form.canal}
              onChange={(e) => handleChange('canal', e.target.value)}
              className="form-input"
              required
            >
              {CANAIS.map(canal => (
                <option key={canal} value={canal}>{canal}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="template" className="required">Template de Mensagem</label>
            <select
              id="template"
              value={form.template}
              onChange={(e) => handleChange('template', e.target.value)}
              className="form-input"
              required
            >
              <option value="">Selecionar template...</option>
              {templates
                // ✅ usoRestrito exclui templates de fluxo dedicado (ex.:
                // "Vaga Disponível na Fila de Espera", configurado em
                // Configurações > Lista de Espera) -- vincular esses aqui
                // não tem efeito (domínio 'ListaEspera' não tem vocabulário
                // de evento) e só confundia o seletor. Ver comentário
                // completo em server/models/TemplateMensagem.js.
                .filter(t => t.canal === form.canal && !t.usoRestrito)
                .map(template => (
                  <option key={template._id} value={template._id}>
                    {template.nome} ({template.canal})
                  </option>
                ))
              }
            </select>
          </div>
        </div>
      </fieldset>

      {['Agendamento', 'Profissional'].includes(form.dominio) && (
        <fieldset className="form-section">
          <legend className="form-section-title">Período de Envio (opcional)</legend>
          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id="usarPeriodoProprio"
              checked={usarPeriodoProprio}
              onChange={(e) => setUsarPeriodoProprio(e.target.checked)}
              className="form-checkbox"
            />
            <label htmlFor="usarPeriodoProprio">Usar horário próprio (em vez do padrão do template)</label>
          </div>

          {usarPeriodoProprio && (
            <PeriodoEnvioFields
              value={periodoEnvio}
              onChange={setPeriodoEnvio}
              permitirAntesDepois
              idPrefix="config-periodo-envio"
            />
          )}
        </fieldset>
      )}

      <div className="form-actions">
        <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
          <XCircle size={18} className="icon" />Cancelar
        </button>
        <FormStepNavigation
          currentStep={1}
          totalSteps={1}
          onBack={() => {}}
          onNext={() => {}}
          isSubmitting={isPending}
          submitLabel={currentId ? 'Atualizar' : 'Criar Configuração'}
        />
      </div>
    </form>
  );
};

export default ComunicacaoAutomaticaForm;
